import { Translations } from '@domain/admission';
import { AdmissionGroup } from '@domain/admission-group';
import { getLanguageTranslationKey } from '@utils/get-language-trandlation-key/get-language-translation-key';
import { TFunction } from 'i18next';

type DuplicateCheckProps = {
  value: string;
  language: string;
  sectionGroups: AdmissionGroup[];
  excludeGroupId?: string;
};

export const hasDuplicateGroupName = ({
  value,
  language,
  sectionGroups,
  excludeGroupId,
}: DuplicateCheckProps): boolean => {
  const normalized = value.trim().toLowerCase();

  return sectionGroups.some((group) => {
    const groupName = group.name?.[language];
    if (!groupName) return false;

    return group.id !== excludeGroupId && groupName.trim().toLowerCase() === normalized;
  });
};

type CreateValidationProps = {
  sectionGroups: AdmissionGroup[];
  t: TFunction;
  excludeGroupId?: string;
};

/**
 * Returns the `validation` object expected by EditFieldModal
 * to validate group names (duplicate check only).
 */
export const createGroupNameValidation = ({ sectionGroups, t, excludeGroupId }: CreateValidationProps) => ({
  validate: (value: string, language: string, _allValues: Translations) => {
    if (!value) return true;

    if (!value.trim()) return t('errors:fieldEmptyError');

    const hasDuplicate = hasDuplicateGroupName({
      value,
      language,
      sectionGroups,
      excludeGroupId,
    });

    if (!hasDuplicate) return true;

    return t('admissionGroups:groupModalDuplicateNameError', {
      language: t(getLanguageTranslationKey(language)).toLowerCase(),
    });
  },
});

type SelectionValidationProps = {
  name: string;
  language: string;
  sectionGroups: AdmissionGroup[];
  t: TFunction;
  excludeGroupId?: string;
  tRequiredKey?: string; // ej: 'errors:fieldEmptyError'
};

/**
 * Version designed for AdmissionCard + RadioListModal: returns the error message
 * (or empty string) for a specific name and language.
 */
export const getGroupNameErrorForSelection = ({
  name,
  language,
  sectionGroups,
  t,
  excludeGroupId,
  tRequiredKey,
}: SelectionValidationProps): string => {
  const trimmed = name.trim();

  if (!trimmed) {
    return tRequiredKey ? t(tRequiredKey) : '';
  }

  const hasDuplicate = hasDuplicateGroupName({
    value: trimmed,
    language,
    sectionGroups,
    excludeGroupId,
  });

  if (hasDuplicate) {
    return t('admissionGroups:groupModalDuplicateNameError', {
      language: t(getLanguageTranslationKey(language)).toLowerCase(),
    });
  }

  return '';
};
